/**
 * "every 2 weeks on Monday" — the localized CADENCE clause, from rule options.
 *
 * WHY IT LIVES HERE AND NOT NEXT TO ITS FIRST CALLER
 * This logic started life inside `calendar/grid/recurrence-sentence.ts`, which
 * imports `fetchRule` from `schedule-picker/recurrence-utils`. The compose
 * dialog's own preview (`generateRecurrenceText`, in that same recurrence-utils)
 * needs the identical clause, and importing the calendar module back into
 * recurrence-utils would close an import cycle. So the part that has no
 * dependencies — the grammar — is here, with zero imports from either side, and
 * both callers reach down into it.
 *
 * WHAT IT IS FOR
 * The compose dialog's preview did not mention INTERVAL at all: a rule of "every
 * 2 weeks on Monday" previewed as "This message will be sent every Monday", so
 * the customer was shown a WEEKLY schedule immediately before committing a
 * fortnightly one. That is the defect this clause closes, and it is why the
 * interval is passed as `count` — each locale then expresses it in its own
 * plural forms, and Hebrew reaches its dual ("כל שבועיים") instead of a literal
 * "כל 2 שבועות".
 *
 * Never throws and never guesses: an exotic or unreadable rule yields '', and
 * every caller treats '' as "say nothing" rather than leaking a half-sentence.
 */

import { Frequency } from 'rrule';

export type Translate = (key: string, options?: Record<string, unknown>) => string;

const KEY = 'calendar.grid.recurrence.';

/** rrule counts weekdays from Monday (MO=0); moment counts from Sunday. */
export const toMomentWeekday = (rruleWeekday: number): number => (rruleWeekday + 1) % 7;

function joinList(items: string[], t: Translate): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    const separator = t(`${KEY}listSeparator`);
    const and = t(`${KEY}listAnd`);
    return items.slice(0, -1).join(separator) + and + items[items.length - 1];
}

/**
 * "on Monday" / "ביום שני" / "בימים שני ורביעי".
 *
 * The preposition is a separate key because Hebrew inflects it with the NUMBER
 * OF DAYS (ביום / בימים) while the sentence around it inflects with the
 * interval — two different counts, so two lookups.
 *
 * `weekdayNames` is Sunday-first and supplied by the caller rather than read
 * from moment's GLOBAL locale: that global is updated from an i18next event
 * which can land after a render, and the compose dialog's preview already goes
 * to the trouble of formatting its dates against the render's own language.
 * Taking the day names from a different source would have re-introduced exactly
 * the English-day-name-in-a-Hebrew-sentence bug that formatting fixed.
 */
export function onDaysPhrase(byweekday: unknown, weekdayNames: string[], t: Translate): string {
    const raw = Array.isArray(byweekday) ? byweekday : [byweekday];
    const indexes = raw
        .map(entry => (typeof entry === 'number' ? entry : (entry as { weekday?: unknown } | null)?.weekday))
        .filter((value): value is number => typeof value === 'number')
        .map(toMomentWeekday)
        .sort((a, b) => a - b);
    if (indexes.length === 0) return '';
    const days = joinList(indexes.map(index => weekdayNames[index]).filter(Boolean), t);
    if (!days) return '';
    return t(`${KEY}onDays`, { count: indexes.length, days });
}

/** Exactly the fields of an rrule `Options` this grammar reads. */
export interface CadenceOptions {
    freq?: Frequency | number | null;
    interval?: number | null;
    byweekday?: unknown;
    bymonthday?: unknown;
}

export function recurrenceCadenceSentence(
    options: CadenceOptions | null | undefined,
    weekdayNames: string[],
    t: Translate,
): string {
    if (!options) return '';
    // The interval drives the plural form: 1 -> "every week", 2 -> the locale's
    // dual, 3+ -> "every 3 weeks".
    const count = Math.max(1, options.interval || 1);

    switch (options.freq) {
        case Frequency.DAILY:
            return t(`${KEY}daily`, { count });

        case Frequency.WEEKLY: {
            const days = onDaysPhrase(options.byweekday, weekdayNames, t);
            return days
                ? t(`${KEY}weeklyOnDays`, { count, days })
                : t(`${KEY}weekly`, { count });
        }

        case Frequency.MONTHLY: {
            const monthdays = Array.isArray(options.bymonthday)
                ? options.bymonthday
                : [options.bymonthday];
            const day = monthdays.find(value => typeof value === 'number' && value > 0);
            return typeof day === 'number'
                ? t(`${KEY}monthlyOnDay`, { count, day })
                : t(`${KEY}monthly`, { count });
        }

        case Frequency.YEARLY:
            return t(`${KEY}yearly`, { count });

        default:
            // HOURLY / MINUTELY / SECONDLY are not offered by the product;
            // say nothing rather than something wrong.
            return '';
    }
}
